// react
import { MouseEvent, useState, useEffect, useContext } from 'react';

// types
import { EntityUser, Loan, User } from '../../../@types';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// i18n
import { useTranslation } from 'react-i18next';

// material
import { Alert, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// cards
import { LoanEntities } from '../../ui/LoanEntities/LoanEntities';
import { LoanAccounts } from '../../ui/LoanAccounts/LoanAccounts';

// props
export interface DialogEnableLoanProps {
	isOpen: boolean;
	loan: Loan;
	onClose: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

// Remove data once users are fetched
export const DialogEnableLoan = ({ isOpen, loan, onClose, onSuccess }: DialogEnableLoanProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// state
	const [entities, setEntities] = useState<EntityUser[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// set loading
		setIsLoading(true);

		// define fetch requests array
		const fetchRequests: Promise<Response>[] = [];

		// define patch loan request
		const fetchPatchLoan = fetch(`${process.env.REACT_APP_API_URL}/loans/${loan.id}`, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
			body: JSON.stringify({ status: 'ENABLED' }),
		});

		// add patch loan request
		fetchRequests.push(fetchPatchLoan);

		// for each entity
		for (let t = 0; t < entities.length; t++) {
			// for each user
			for (let i = 0; i < entities[t].users.length; i++) {
				// define user
				const user = entities[t].users[i];

				// define patch user request
				const fetchPatchUser = fetch(`${process.env.REACT_APP_API_URL}/users/${user.id}`, {
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify({
						projects: [
							{
								id: project?.id,
								status: 'INVITED',
							},
						],
					}),
				});

				// add patch user request to array
				fetchRequests.push(fetchPatchUser);
			}
		}

		console.log('FETCH REQUESTS', fetchRequests);

		// wait for all fetch requests
		Promise.all(fetchRequests)
			.then(async (responses) => {
				// define array of json responses
				const resJsonArray = [];

				// for each response
				for (let i = 0; i < responses.length; i++) {
					// read response as json
					const res = await responses[i].json();
					resJsonArray.push(res);
				}

				console.log('JSON', resJsonArray);

				// set loading
				setIsLoading(false);

				if (resJsonArray[0].data) {
					if (onSuccess) {
						onSuccess();
					}

					// set success banner
					setBanner({
						hasClose: true,
						message: t('alert.enableLoanSuccess', { id: loan.code }),
						severity: 'success',
					});

					// close dialog
					if (onClose) {
						onClose(e);
					}
				}
			})
			.catch((error) => {
				let message = t('error.default');

				if (error.id === 'LOAN_UNAUTHORIZED_PATCH') {
					message = t('error.unauthorized');
				}

				// set error
				setError(message);
			});
	};

	// fetch users when loan is updated
	useEffect(() => {
		if (loan.entities) {
			const entitiesUsers: EntityUser[] = [];
			for (const entity of loan.entities) {
				if (entity.id) {
					// fetch users
					fetch(
						`${process.env.REACT_APP_API_URL}/users?entities=${entity.id}&fields=id,role`,
						{
							headers: {
								Authorization: `Bearer ${accessToken}`,
								User: String(idToken),
							},
						}
					)
						.then((res) => {
							return res.json();
						})
						.then((res) => {
							if (res.status === 200) {
								// define users
								const users = res.data as User[];

								// put users in entityUser
								entitiesUsers.push({
									...entity,
									users,
								});
							}
						});
				}
			}
			setEntities(entitiesUsers);
		}
	}, [accessToken, idToken, loan]);

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: onClose,
					variant: 'text',
				},
				{
					children: t('Enable'),
					loading: isLoading,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			content={
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography color="brand.grey.500">{t('enableLoan.intro')}</Typography>
					</Grid>
					<LoanEntities entities={entities} />
					<LoanAccounts loan={loan} />
					{error && (
						<Grid item xs={12}>
							<Alert severity="error">{error}</Alert>
						</Grid>
					)}
				</Grid>
			}
			IconCircleProps={{
				type: 'loan',
			}}
			isOpen={isOpen}
			maxWidth={600}
			onClose={onClose}
			title={{
				children: `${t('enableLoan')} ${loan.code}`,
			}}
		/>
	);
};
